// Admin panel kit shared by every admin screen (pages/admin/*.tsx): a Card with
// an optional title row, a padded body, and flush content (DataTable, forms,
// code wells) between hairlines.
// Structure: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/card.tsx (CardHeader / CardTitle / CardContent) — MIT.

import type { ComponentProps, ReactNode } from "react";
import { Card, CardTitle } from "@/ui";
import { cn } from "@/lib/cn";
import { panelInsetClass } from "./classes";

export function AdminPanel({
  title,
  titleAs = "h2",
  className,
  children,
}: {
  title?: string;
  /** h3 when the panel sits under a section h2 (Overview). */
  titleAs?: "h2" | "h3";
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card padded={false} className={cn("min-w-0 gap-0 overflow-hidden", className)}>
      {title && (
        <div className={cn(panelInsetClass, "border-b border-line py-4")}>
          <CardTitle as={titleAs} className={titleAs === "h3" ? "text-base" : undefined}>
            {title}
          </CardTitle>
        </div>
      )}
      {children}
    </Card>
  );
}

export function PanelBody({ className, ...rest }: ComponentProps<"div">) {
  return <div className={cn(panelInsetClass, "py-5", className)} {...rest} />;
}

/** "Nothing here yet" line inside a titled panel. */
export function PanelEmpty({ children }: { children: ReactNode }) {
  return <p className={cn(panelInsetClass, "py-5 text-sm text-fg-2")}>{children}</p>;
}
